// UX-Vorschlag P3: EIN Detailbereich fuer persoenliche UND Projektaufgaben.
// Oeffnet rechts neben der Liste; ↑/↓ (bzw. j/k) wechselt zur naechsten
// Aufgabe, ohne zu schliessen; Esc schliesst. Nur hinter uxEnabled("ux_web_p3")
// — TaskModal und das Lists-Detail bleiben fuer classic unveraendert und sind
// ueber „Vollständig öffnen" weiter erreichbar (alle Spezialfelder).
//
// Gleich fuer beide Typen: Kopfzeile (Typ, Herkunft, Ticket, Aktionen), die
// vier Kernfelder (Status, Priorität, Fällig, Zuständig), „Schritte",
// Typspezifisches eingeklappt. Aenderungen laufen ueber ResolverContext.patch
// — denselben Weg wie die Listenzeilen, also dieselben Server-Aufrufe.

import { Fragment } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { ArrowUpRight, ChevronDown, ChevronUp, Plus, Trash2, X } from "lucide-preact";
import type { TaskPriority } from "@juuplan/shared";
import { TASK_PRIORITIES } from "@juuplan/shared";
import { Button } from "../ui/Button";
import { AssigneePicker } from "../AssigneePicker";
import { MarkdownEditor } from "../MarkdownEditor";
import { RichTextEditor } from "../RichTextEditor";
import { TASK_PRIORITY_LABEL } from "../../lib/format";
import { toastError } from "../../lib/toast";
import type { ResolverContext, TaskRecord } from "../../lib/taskRecord";
import { buildDetailModel, stepsProgress, type DetailStep } from "../../lib/taskDetailModel";
import { saveDescription, stepsAdapterFor } from "../../lib/taskDetailSteps";

export interface TaskDetailPanelProps {
  record: TaskRecord;
  ctx: ResolverContext;
  onClose: () => void;
  /** ↑/↓: −1 = vorige, +1 = naechste Aufgabe der sichtbaren Liste. */
  onNavigate: (dir: 1 | -1) => void;
  /** Bisheriger Voll-Editor (Projekt-Modal bzw. Listen-Detail). */
  onOpenFull: (record: TaskRecord) => void;
  /** Nach Aenderungen, die die Liste nicht selbst nachlaedt (Schritte, Beschreibung). */
  onChanged?: () => void;
}

function isEditable(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true;
}

const label = "text-xs font-medium text-[color:var(--color-on-surface-variant)]";
const control =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] sm:text-sm";

export function TaskDetailPanel(props: TaskDetailPanelProps): preact.JSX.Element {
  const { record, ctx, onClose, onNavigate, onOpenFull, onChanged } = props;
  const model = buildDetailModel(record, {
    projectName: (id) => ctx.resolveProject(id)?.name,
    listName: (id) => ctx.resolveList?.(id)?.name,
  });
  const statuses = ctx.resolveStatuses?.(record) ?? [];

  const [title, setTitle] = useState(record.title);
  const [desc, setDesc] = useState(model.description.value);
  const [steps, setSteps] = useState<DetailStep[] | null>(null);
  const [stepsError, setStepsError] = useState<string | null>(null);
  const [newStep, setNewStep] = useState("");
  const [busy, setBusy] = useState(false);
  const titleRef = useRef<HTMLTextAreaElement>(null);

  // Titelfeld waechst mit dem Text (kein festes `rows`, kein Scrollbalken).
  const fitTitle = (): void => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  useLayoutEffect(fitTitle, [title]);

  // Neue Aufgabe gewaehlt: lokalen Zustand neu aufsetzen und Schritte laden.
  useEffect(() => {
    setTitle(record.title);
    setDesc(model.description.value);
    setSteps(null);
    setStepsError(null);
    void loadSteps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.uid]);

  // Tastatur: ↑/↓ bzw. k/j wechseln, Esc schliesst — nie aus Eingabefeldern.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.metaKey || e.ctrlKey || e.altKey || isEditable(e.target)) return;
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        onNavigate(1);
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        onNavigate(-1);
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNavigate, onClose]);

  const adapter = stepsAdapterFor(record);

  async function loadSteps(): Promise<void> {
    try {
      setSteps(await stepsAdapterFor(record).load());
    } catch (e) {
      // GET-Fehler toastet api() nicht: sichtbar machen statt „keine Schritte".
      setStepsError(e instanceof Error ? e.message : "Unbekannter Fehler");
    }
  }

  const runStep = async (fn: () => Promise<unknown>): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      await loadSteps();
      onChanged?.();
    } catch {
      /* Mutation: api() hat getoastet */
    } finally {
      setBusy(false);
    }
  };

  const commitTitle = (): void => {
    const t = title.trim();
    if (!t || t === record.title) {
      setTitle(record.title);
      return;
    }
    void ctx.patch(record, { title: t });
  };

  const commitDesc = async (value: string): Promise<void> => {
    if (value === model.description.value) return;
    try {
      await saveDescription(record, value);
      onChanged?.();
    } catch {
      toastError("Beschreibung konnte nicht gespeichert werden");
    }
  };

  const progress = steps ? stepsProgress(steps) : null;

  return (
    <section
      aria-label={`Details: ${record.title}`}
      class="flex max-h-[calc(100vh-8rem)] flex-col overflow-y-auto rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]"
    >
      {/* Kopfzeile — fuer beide Typen gleich. */}
      <header class="flex items-start gap-2 border-b border-[color:var(--color-outline-variant)] px-4 py-3">
        <div class="min-w-0 flex-1">
          {/* Eine Zeile, Herkunft kuerzt mit „…" — mit flex-wrap rutschte
              „· Herkunft" sonst allein in eine zweite Zeile. */}
          <p class="flex min-w-0 items-center gap-1.5 whitespace-nowrap text-xs text-[color:var(--color-on-surface-variant)]">
            <span class="shrink-0 font-medium">{model.typeLabel}</span>
            {model.origin && (
              <span class="min-w-0 truncate" title={model.origin}>
                · {model.origin}
              </span>
            )}
            {model.ticket && <span class="shrink-0 tabular-nums">· {model.ticket}</span>}
          </p>
          {/* Mehrzeilig statt <input>: lange Titel wurden abgeschnitten.
              Enter speichert weiterhin, ein Zeilenumbruch im Titel ist nicht vorgesehen. */}
          <textarea
            ref={titleRef}
            rows={1}
            value={title}
            onInput={(e) => {
              setTitle((e.target as HTMLTextAreaElement).value.replace(/\n/g, " "));
              fitTitle();
            }}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (e.target as HTMLTextAreaElement).blur();
              }
            }}
            aria-label="Titel"
            maxLength={500}
            class="mt-1 block w-full resize-none overflow-hidden rounded bg-transparent px-1 -mx-1 text-base font-semibold leading-snug text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          />
        </div>
        <div class="flex shrink-0 items-center gap-0.5">
          <Button variant="quiet" size="xs" icon={ChevronUp} onClick={() => onNavigate(-1)} aria-label="Vorige Aufgabe" title="Vorige Aufgabe (↑)" />
          <Button variant="quiet" size="xs" icon={ChevronDown} onClick={() => onNavigate(1)} aria-label="Nächste Aufgabe" title="Nächste Aufgabe (↓)" />
          <Button variant="quiet" size="xs" icon={ArrowUpRight} onClick={() => onOpenFull(record)} aria-label="Vollständig öffnen" title="Vollständig öffnen" />
          <Button variant="quiet" size="xs" icon={X} onClick={onClose} aria-label="Details schließen" title="Schließen (Esc)" />
        </div>
      </header>

      {/* Die vier Kernfelder. */}
      <div class="grid grid-cols-2 gap-3 px-4 py-3">
        <label class="flex flex-col gap-1">
          <span class={label}>Status</span>
          {statuses.length > 0 ? (
            <select
              class={control}
              value={record.status.id ?? ""}
              onChange={(e) => void ctx.patch(record, { statusId: Number((e.target as HTMLSelectElement).value) })}
            >
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : (
            <select
              class={control}
              value={record.done ? "done" : "open"}
              onChange={(e) => void ctx.patch(record, { done: (e.target as HTMLSelectElement).value === "done" })}
            >
              <option value="open">Offen</option>
              <option value="done">Erledigt</option>
            </select>
          )}
        </label>
        <label class="flex flex-col gap-1">
          <span class={label}>Priorität</span>
          <select
            class={control}
            value={record.priority ?? ""}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              void ctx.patch(record, { priority: v === "" ? null : (v as TaskPriority) });
            }}
          >
            <option value="">Keine</option>
            {TASK_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {TASK_PRIORITY_LABEL[p]}
              </option>
            ))}
          </select>
        </label>
        <label class="flex flex-col gap-1">
          <span class={label}>Fällig</span>
          <input
            type="date"
            class={control}
            value={record.dueDate?.slice(0, 10) ?? ""}
            onChange={(e) => {
              const v = (e.target as HTMLInputElement).value;
              void ctx.patch(record, { dueDate: v === "" ? null : v });
            }}
          />
        </label>
        <div class="flex flex-col gap-1">
          <span class={label}>Zuständig</span>
          <AssigneePicker
            value={record.assigneeIds}
            onChange={(ids) => ctx.patch(record, { assigneeIds: ids })}
            label="Zuständige ändern"
            emptyLabel="Niemand"
            projectId={record.projectId ?? undefined}
          />
        </div>
      </div>

      {/* Beschreibung — Format bleibt pro Typ (siehe lib/taskDetailModel.ts). */}
      <div class="border-t border-[color:var(--color-outline-variant)] px-4 py-3">
        <p class={`${label} mb-1`}>Beschreibung</p>
        {model.description.format === "html" ? (
          <RichTextEditor
            value={desc}
            docKey={record.uid}
            placeholder="Notizen …"
            onCommit={(html) => void commitDesc(html ?? "")}
          />
        ) : (
          <div onFocusOut={() => void commitDesc(desc)}>
            <MarkdownEditor value={desc} onChange={setDesc} rows={5} placeholder="Beschreibung …" />
          </div>
        )}
      </div>

      {/* Ein Konzept „Schritte" — Checkliste bzw. persoenliche Schritte. */}
      <div class="border-t border-[color:var(--color-outline-variant)] px-4 py-3">
        <p class={`${label} mb-1 flex items-center gap-2`}>
          Schritte
          {progress && progress.total > 0 && (
            <span class="tabular-nums">
              {progress.done}/{progress.total}
            </span>
          )}
        </p>
        {stepsError ? (
          <p class="text-sm text-[color:var(--color-error)]">
            Schritte konnten nicht geladen werden: {stepsError}{" "}
            <button type="button" class="underline" onClick={() => void loadSteps()}>
              Erneut laden
            </button>
          </p>
        ) : steps === null ? (
          <p class="text-sm italic text-[color:var(--color-on-surface-variant)]">Lädt…</p>
        ) : (
          <ul class="flex flex-col">
            {steps.map((s) => (
              <li key={s.id} class="group flex items-center gap-2 py-1">
                <input
                  type="checkbox"
                  checked={s.done}
                  disabled={busy}
                  onChange={() => void runStep(() => adapter.setDone(s, !s.done))}
                  aria-label={`${s.title} ${s.done ? "als offen markieren" : "abhaken"}`}
                />
                <span
                  class={`min-w-0 flex-1 truncate text-sm ${
                    s.done ? "text-[color:var(--color-on-surface-variant)] line-through" : "text-[color:var(--color-on-surface)]"
                  }`}
                >
                  {s.title}
                </span>
                {s.canPromote && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void runStep(() => adapter.promote(s))}
                    class="shrink-0 rounded px-1 text-xs text-[color:var(--color-primary)] opacity-100 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
                  >
                    {adapter.promoteLabel}
                  </button>
                )}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void runStep(() => adapter.remove(s))}
                  aria-label={`${s.title} entfernen`}
                  class="shrink-0 rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-error)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                >
                  <Trash2 size={13} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <form
          class="mt-1 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const t = newStep.trim();
            if (!t) return;
            void runStep(async () => {
              await adapter.add(t);
              setNewStep("");
            });
          }}
        >
          <Plus size={14} class="shrink-0 text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
          <input
            type="text"
            value={newStep}
            onInput={(e) => setNewStep((e.target as HTMLInputElement).value)}
            placeholder="Schritt hinzufügen …"
            aria-label="Neuer Schritt"
            maxLength={500}
            class="min-w-0 flex-1 bg-transparent text-base text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none sm:text-sm"
          />
        </form>
      </div>

      {/* Typspezifisches — eingeklappt. */}
      <details class="border-t border-[color:var(--color-outline-variant)] px-4 py-3">
        <summary class={`${label} cursor-pointer`}>Weitere Felder</summary>
        {model.more.length === 0 ? (
          <p class="mt-2 text-sm text-[color:var(--color-on-surface-variant)]">Keine weiteren Angaben.</p>
        ) : (
          <dl class="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            {model.more.map((f) => (
              <Fragment key={f.label}>
                <dt class="text-[color:var(--color-on-surface-variant)]">{f.label}</dt>
                <dd class="text-[color:var(--color-on-surface)]">{f.value}</dd>
              </Fragment>
            ))}
          </dl>
        )}
        <button
          type="button"
          onClick={() => onOpenFull(record)}
          class="mt-2 text-xs text-[color:var(--color-primary)] hover:underline"
        >
          Alle Felder im vollständigen Editor bearbeiten
        </button>
      </details>
    </section>
  );
}
